import React, { useEffect, useState, useMemo } from "react";
import {
  getTeachersForSchedule,
} from "../../api/markschedule/marksApi";
import {
  getTeachersForSchedulePortal,
} from "../../api/markschedule/marksPortalApi";
import { getAllAssignments } from "../../api/teachersubjects/teacherSubjectApi";
import { getAllAssignmentsPortal } from "../../api/teachersubjects/teacherSubjectPortalApi";
import { pickList, toId } from "../../utils/markschedule/marksHelpers";
import { Select } from "../../components/ui";

/**
 * Marks teacher picker — ONLY from TeacherSubjectAssignment.
 * Prefer schedule-scoped endpoint (subject inferred from schedule).
 * Fallback: teacher-subjects filtered by subjectId + sectionId.
 * No free staff id input.
 */
export default function TeacherSubjectSelect({
  isPortal = false,
  scheduleId = "",
  subjectId = "",
  sectionId = "",
  value = "",
  onChange,
  disabled = false,
  label = "Teacher (subject assignment)",
  required = false,
}) {
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!sectionId && !scheduleId) {
      setTeachers([]);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        let list = [];

        if (scheduleId) {
          const res = isPortal
            ? await getTeachersForSchedulePortal(scheduleId, {
                sectionId: sectionId || undefined,
              })
            : await getTeachersForSchedule(scheduleId, {
                sectionId: sectionId || undefined,
              });
          list = pickList(res, ["teachers", "data"]);
        } else if (subjectId && sectionId) {
          const res = isPortal
            ? await getAllAssignmentsPortal({
                subjectId,
                sectionId,
                isActive: true,
              })
            : await getAllAssignments({
                subjectId,
                sectionId,
                isActive: true,
              });
          list = pickList(res, ["assignments", "data", "teacherSubjects"]);
        }

        if (cancelled) return;

        const normalized = list
          .map((t) => {
            const staff = t.staff || t.staffId || t;
            const id = toId(staff) || toId(t.staffId);
            if (!id) return null;
            return {
              staffId: id,
              name: staff.name || t.name || "Teacher",
              staffCode: staff.staffCode || t.staffCode || "",
              assignmentId: toId(t),
            };
          })
          .filter(Boolean);

        const seen = new Set();
        const unique = [];
        for (const n of normalized) {
          if (seen.has(n.staffId)) continue;
          seen.add(n.staffId);
          unique.push(n);
        }
        setTeachers(unique);
      } catch (e) {
        if (!cancelled) setTeachers([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [scheduleId, subjectId, sectionId, isPortal]);

  const teacherOptions = useMemo(() => {
    const placeholderLabel = loading
      ? "Loading teachers…"
      : !sectionId && !scheduleId
      ? "Select section first"
      : teachers.length === 0
      ? "No subject teacher for this section"
      : "Select teacher";

    return [
      { label: placeholderLabel, value: "" },
      ...teachers.map((t) => ({
        label: `${t.name}${t.staffCode ? ` (${t.staffCode})` : ""}`,
        value: t.staffId,
      })),
    ];
  }, [loading, sectionId, scheduleId, teachers]);

  return (
    <div className="space-y-1">
      <Select
        label={label}
        options={teacherOptions}
        value={value || ""}
        disabled={disabled || loading || (!sectionId && !scheduleId)}
        required={required}
        onChange={(val) => onChange?.(val)}
      />
      {!loading && sectionId && teachers.length === 0 && (
        <p className="text-[11px] text-amber-700">
          No active TeacherSubjectAssignment for this subject + section.
        </p>
      )}
    </div>
  );
}